import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Form, Button, Spinner, Alert, Card } from 'react-bootstrap';
import { Box } from './backlinkCheckerStyle';
import axios from 'axios';
import { isInIframe } from '../../utils/is-in-iframe';
import { ToolsHeader } from '../components/ToolsHeader';

interface BacklinkMetrics {
  domain: string;
  totalBacklinks: number;
  referringDomains: number;
  qualityBacklinks: number;
  spamScore: number;
}

const formatNumber = (num: number): string => {
  if (num >= 1_000_000) return (num / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
  if (num >= 1_000) return (num / 1_000).toFixed(1).replace(/\.0$/, '') + 'K';
  return num.toString();
};

const BacklinkChecker: React.FC = () => {
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<BacklinkMetrics | null>(null);

  useEffect(() => {
    const height = document.body.scrollHeight;
    window.parent.postMessage({ type: 'setHeight', height }, '*');
  }, [result]);

  const handleCheckBacklinks = async () => {
    if (!url) return;
    setLoading(true);
    setError('');
    setResult(null);

    try {
      const baseUrl = process.env.REACT_APP_BASE_API || 'http://localhost:3000';
      const response = await axios.get(
        `${baseUrl}/api/backlink-checker/backlink?domain=${encodeURIComponent(url)}&type=domain`
      );
      setResult(response.data);
    } catch (err: any) {
      setError('Failed to fetch backlink data. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setUrl('');
    setError('');
    setResult(null);
  };

  return (
    <Box className={`${!isInIframe ? 'defaultText' : 'interDisplay'}`}>
      <Container>
        {!isInIframe && (
          <ToolsHeader
            title="Website Authority & Backlink Checker"
            description="This tool analyzes your website’s backlinks, referring domains, and spam score to help you understand its authority and link profile quality."
          />
        )}

        <Form
          onSubmit={(e) => {
            e.preventDefault();
            handleCheckBacklinks();
          }}
        >
          <Row className="gx-2 mb-3">
            <Col xs={12} md={8} className="mb-2 mb-md-0">
              <Form.Control
                type="text"
                placeholder="Enter domain (e.g., example.com)"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                style={{
                  height: '52px',
                  fontSize: '1rem',
                  borderRadius: '8px',
                  border: '2px solid #e9ecef',
                  backgroundColor: '#ffffff',
                  color: '#212529',
                  transition: 'all 0.3s ease',
                }}
              />
            </Col>
            <Col xs={12} md={4}>
              <Button
                variant="success"
                onClick={handleCheckBacklinks}
                style={{
                  height: '52px',
                  fontWeight: 600,
                  fontSize: '1rem',
                  width: '100%',
                  borderRadius: '8px',
                  backgroundColor: '#8CC540',
                  border: 'none',
                  color: '#fff',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 4px 12px rgba(140, 197, 64, 0.3)',
                }}
                disabled={loading}
              >
                {loading ? <Spinner size="sm" animation="border" /> : 'Check Backlinks'}
              </Button>
            </Col>
          </Row>
        </Form>

        <div className="text-center mb-3" style={{ color: '#6c757d', fontSize: '0.9rem' }}>
          For example:{' '}
          <span style={{ color: '#8CC540', fontWeight: 600 }}>coozmoo.com</span>,{' '}
          <span style={{ color: '#8CC540', fontWeight: 600 }}>ahrefs.com</span>
        </div>

        {error && <Alert variant="danger">{error}</Alert>}

        {result && (
          <div className="result-section text-center mt-5">
            <h4 style={{ color: '#343a40', fontWeight: 600, marginBottom: '20px' }}>
              Backlink Report for{' '}
              <a
                href={`https://${result.domain}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#8CC540', textDecoration: 'none', fontWeight: 600 }}
              >
                {result.domain}
              </a>
            </h4>

            <Row className="g-4 justify-content-center">
              {[
                { label: 'Total Backlinks', value: formatNumber(result.totalBacklinks) },
                { label: 'Referring Domains', value: formatNumber(result.referringDomains) },
                { label: 'Quality Backlinks', value: `${result.qualityBacklinks}%` },
                { label: 'Spam Score', value: `${result.spamScore}%` },
              ].map((item, index) => (
                <Col xs={12} sm={6} md={4} lg={3} key={index}>
                  <Card className="card">
                    <Card.Body>
                      <Card.Title className="card-title">{item.label}</Card.Title>
                      <Card.Text className="card-value">{item.value}</Card.Text>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>

            <div className="mt-4">
              <Button
                onClick={handleReset}
                style={{
                  backgroundColor: '#41490f',
                  borderRadius: '8px',
                  color: '#fff',
                  border: 'none',
                  padding: '10px 24px',
                  fontWeight: 600,
                }}
              >
                Reset
              </Button>
            </div>
          </div>
        )}
      </Container>
    </Box>
  );
};

export default BacklinkChecker;
